import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Polyline, Tooltip, useMap } from "react-leaflet";
import { Plus, Minus, Layers, Compass, Hammer, Eye, RotateCcw } from "lucide-react";
import { quietIcon, numberedIcon } from "../../lib/markers";
import { typeMeta } from "../../lib/adventureConstants";
import CountryBoundary from "./CountryBoundary";

const MAP_MODES = [
  { id: "explore", label: "Explore", icon: Compass },
  { id: "build", label: "Build", icon: Hammer },
  { id: "review", label: "Review", icon: Eye },
];

function FitToRegion({ center, zoom }) {
  const map = useMap();
  useEffect(() => { map.setView(center, zoom); }, [center, zoom, map]);
  return null;
}

export default function AdventureMap({
  region, locations, modulesById, locationsById, items,
  onDropModule, onMarkerClick, mode, setMode, onReset, onLocationClick,
}) {
  const [map, setMap] = useState(null);
  const [dropOn, setDropOn] = useState(false);

  // ordered module items -> numbered stops
  const moduleItems = items.filter((it) => it.kind !== "transport");
  const stops = moduleItems.map((it, idx) => {
    const m = modulesById[it.module_id];
    const loc = locationsById[m?.location_id];
    return loc ? { n: idx + 1, lat: loc.lat, lng: loc.lng, name: m.name, type: m.type } : null;
  }).filter(Boolean);

  const selectedLocIds = new Set(moduleItems.map((it) => modulesById[it.module_id]?.location_id));
  const routeLine = useMemo(() => stops.map((s) => [s.lat, s.lng]), [stops]);

  return (
    <div
      className="relative h-full w-full"
      data-testid="adventure-map"
      onDragOver={(e) => { e.preventDefault(); setDropOn(true); }}
      onDragLeave={() => setDropOn(false)}
      onDrop={(e) => {
        e.preventDefault(); setDropOn(false);
        const id = e.dataTransfer.getData("moduleId");
        if (id) onDropModule(id);
      }}
    >
      <div className={`absolute inset-0 ${dropOn ? "drop-active" : ""}`}>
        <MapContainer
          center={region.center} zoom={region.zoom} ref={setMap}
          style={{ height: "100%", width: "100%" }} zoomControl={false} attributionControl={false}
        >
          <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}" maxZoom={18} />
          <FitToRegion center={region.center} zoom={region.zoom} />
          <CountryBoundary regionId={region.id} />

          {routeLine.length > 1 && (
            <Polyline positions={routeLine} pathOptions={{ color: "#D95A40", weight: 3, opacity: 0.85, dashArray: "1 8", lineCap: "round" }} />
          )}

          {/* quiet opportunity markers */}
          {mode !== "review" && locations.map((loc) => {
            if (selectedLocIds.has(loc.id)) return null;
            return (
              <Marker key={loc.id} position={[loc.lat, loc.lng]} icon={quietIcon(loc.type)}
                eventHandlers={{ click: () => onLocationClick && onLocationClick(loc.id) }}>
                <Tooltip direction="top" offset={[0, -12]}>{loc.name} · click to explore</Tooltip>
              </Marker>
            );
          })}

          {/* numbered selected stops */}
          {stops.map((s) => (
            <Marker
              key={`stop-${s.n}`} position={[s.lat, s.lng]} icon={numberedIcon(s.n)}
              eventHandlers={{ click: () => onMarkerClick && onMarkerClick(s) }}
            >
              <Tooltip direction="top" offset={[0, -30]}>{s.n}. {s.name}</Tooltip>
            </Marker>
          ))}
        </MapContainer>
      </div>

      {/* Mode toggle (top center) */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[500] flex bg-white/80 backdrop-blur-xl border border-white/50 rounded-sm shadow-sm overflow-hidden" data-testid="map-mode-toggle">
        {MAP_MODES.map((m) => {
          const Icon = m.icon;
          return (
            <button
              key={m.id} data-testid={`map-mode-${m.id}`}
              onClick={() => setMode(m.id)}
              className={`flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold tracking-wide transition-colors ${
                mode === m.id ? "bg-moss text-white" : "text-ink-secondary hover:bg-white"
              }`}
            >
              <Icon className="w-3.5 h-3.5" /> {m.label}
            </button>
          );
        })}
      </div>

      {/* Reset (top right) */}
      {items.length > 0 && (
        <button
          data-testid="map-reset-btn"
          onClick={onReset}
          className="absolute top-4 right-4 z-[500] flex items-center gap-1.5 bg-white/80 backdrop-blur-xl border border-white/50 rounded-sm shadow-sm px-3 py-2 text-xs font-semibold tracking-wide text-ink-secondary hover:text-terracotta transition-colors"
        >
          <RotateCcw className="w-3.5 h-3.5" /> Reset
        </button>
      )}

      {/* Zoom controls */}
      <div className="absolute bottom-6 right-4 z-[500] flex flex-col bg-white/80 backdrop-blur-xl border border-white/50 rounded-sm shadow-sm overflow-hidden">
        <button data-testid="map-zoom-in" onClick={() => map?.zoomIn()} className="p-2.5 hover:bg-white transition-colors border-b border-sand-dark"><Plus className="w-4 h-4" /></button>
        <button data-testid="map-zoom-out" onClick={() => map?.zoomOut()} className="p-2.5 hover:bg-white transition-colors"><Minus className="w-4 h-4" /></button>
      </div>

      {/* Legend */}
      <div className="absolute bottom-6 left-4 z-[500] bg-white/80 backdrop-blur-xl border border-white/50 rounded-sm shadow-sm p-3 max-w-[190px]">
        <div className="flex items-center gap-1.5 mb-2 font-mono text-[10px] uppercase tracking-widest text-ink-muted">
          <Layers className="w-3 h-3" /> Legend
        </div>
        <div className="grid grid-cols-2 gap-x-2 gap-y-1">
          {["trek", "culture", "accommodation", "wildlife", "wellness", "adventure"].map((t) => {
            const meta = typeMeta(t);
            return (
              <span key={t} className="flex items-center gap-1.5 text-[11px] text-ink-secondary">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: meta.color }} /> {meta.label}
              </span>
            );
          })}
        </div>
      </div>

      {/* Drop hint */}
      {dropOn && (
        <div className="absolute inset-0 z-[400] flex items-center justify-center pointer-events-none">
          <div className="bg-terracotta text-white font-display font-bold text-xl px-6 py-3 rounded-sm shadow-lg">
            Drop to place on the map
          </div>
        </div>
      )}
    </div>
  );
}
